import { type ReactNode, Suspense } from "react";
import { Toaster } from "sonner";
import { Outlet, HeadContent, Scripts, createRootRouteWithContext } from "@tanstack/react-router";
import { Providers } from "@/providers/providers";
import { APP_NAME, APP_HEADLINE, APP_DESCRIPTION } from "@/config/constants";
import { CookieConsent } from "@/components/ui/cookie-consent";
import { ThemeProvider } from "@/components/themeProvider";
import { RootClientLayout } from "@/RootClientLayout";
import { GlobalError } from "@/global-error";
import { NotFound } from "@/not-found";
import type { QueryClient } from "@tanstack/react-query";
import { seo } from "@/utilities/seo";
import globalCssUrl from "@/styles/globals.css?url";
import "@khmyznikov/pwa-install";
import { PwaInstall } from "@/components/pwaInstall";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  notFoundComponent: NotFound,
  errorComponent: GlobalError,
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no",
      },
      {
        title: `ProgressMade.ai | ${APP_HEADLINE}`,
      },
      ...seo({
        title: "ProgressMade.ai | Schedule Your Health",
        description: APP_DESCRIPTION,
        image: "https://progressmade.ai/meta-image.webp",
        keywords: [
          "Health areas",
          "Health goals",
          "Health routines",
          "Fitness goals",
          "Health goal setting",
          "Health goal planner",
          "AI Health Coach",
          "Health Tech",
          "Smart Fitness",
          "Data Driven Health",
          "Progress Tracker",
          "Metric Tracking",
          "Workout Log",
          "Fitness Log",
          "Activity Tracker",
          "Health Plan",
          "Personalized Health",
          "Health Strategy",
          "Achieve Health Goals",
          "Health Motivation",
          "Fitness Motivation",
          "Wellness Journey",
          "Wellness Wins",
          "Build Health Habits",
          "Consistent Fitness",
          "Project Me",
          "Track Your Progress",
          "Digital Health",
          "Wellness Tech",
          "Personalized Fitness",
        ].join(", "),
      }),
    ],
    links: [
      { rel: "stylesheet", href: globalCssUrl },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/icons/icon-192x192.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/icons/icon-32x32.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/icons/icon-16x16.png",
      },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "icon", href: "/icons/favicon.ico" },
      {
        rel: "theme-color",
        content: "#6d28d9",
      },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <RootClientLayout>
        <Outlet />
      </RootClientLayout>
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />

        {import.meta.env.VITE_ENABLE_REACT_SCAN === "true" && (
          <script src="//unpkg.com/react-scan/dist/auto.global.js" crossOrigin="anonymous" />
        )}

        {/* Add iOS specific meta tags */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={APP_NAME} />

        <script
          defer
          src="https://cloud.umami.is/script.js"
          data-website-id="2cda1b0c-a5e4-48d9-a33b-97ce94a9d913"
        />

        {/* Google tag (gtag.js) */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=AW-17133317133" />
        {/* eslint-disable-next-line react/no-danger */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-17133317133');
            `,
          }}
        />
      </head>
      <body className="bg-background antialiased font-inter">
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <Suspense>
            <Providers>
              <CookieConsent />
              <PwaInstall />
              {children}
              <Toaster richColors theme="light" position="top-right" expand />
            </Providers>
          </Suspense>
        </ThemeProvider>

        <Scripts />
      </body>
    </html>
  );
}
